/**
 * Hook that derives status-badge and denial-risk badge data
 * from the current medical PA order for the single-order header.
 * @module useHeaderBadges
 */
import { useMemo } from "react";
import { BadgeWithIconProps } from "../../../data-model/badgeWithIconProps";
import { AuthOnFile } from "../../../data-model/medicalPaOrdersModel";
import { MedicalPaOrdersAuthStatus } from "../../../enums/medicalPaOrdersAuthStatus";
import { Submission } from "../../../hooks/singleMedicalPaOrderDetails/useSubmissionInformation";
import { getAllOrderDenialRiskLabels } from "../utils/jCodeIssueLabels";

interface UseHeaderBadgesParams {
  authOnFile?: AuthOnFile;
  status?: string;
  authStatusOptions?: BadgeWithIconProps[];
  submissionData?: Submission;
}

/**
 * Computes the auth-status badge and deduplicated denial-risk labels for header chips.
 *
 * @param params.authOnFile - Auth-on-file record containing J-code entries
 * @param params.status - Current auth status string for badge lookup
 * @param params.authStatusOptions - Available badge options to match against
 * @param params.submissionData - Submission data for regimen ICD state denial-risk check
 * @returns Badge props consumed by {@link HeaderStatusBadges}
 */
export const useHeaderBadges = ({
  authOnFile,
  status,
  authStatusOptions,
  submissionData,
}: UseHeaderBadgesParams) => {
  const getStatusBadge = useMemo(() => {
    if (status) {
      return authStatusOptions?.find(
        (option: BadgeWithIconProps) => option.id === status,
      );
    } else {
      return authStatusOptions?.find(
        (option: BadgeWithIconProps) =>
          option.id === MedicalPaOrdersAuthStatus.NotAvailable,
      );
    }
  }, [status, authStatusOptions]);

  const allDenialRiskLabels = useMemo(
    () => getAllOrderDenialRiskLabels(authOnFile, submissionData),
    [authOnFile, submissionData],
  );

  return {
    getStatusBadge,
    allDenialRiskLabels,
  };
};
